
/**
 * @file Keycloak service for authentication and user profile management.
 * @module services/keycloakService
 */

import Keycloak from "keycloak-js";
import logger from "@/utils/logger";

/**
 * Keycloak service options.
 * @typedef {Object} KeycloakOptions
 * @property {string} url - The Keycloak server URL.
 * @property {string} clientId - The Keycloak client ID.
 * @property {string} realm - The Keycloak realm.
 */

const options = {
    url: import.meta.env.VITE_KEYCLOAK_URL,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
    realm: import.meta.env.VITE_KEYCLOAK_REALM
};

const keycloak = new Keycloak(options);
let store = null;

/**
 * Initializes the Keycloak service.
 * @async
 * @function init
 * @returns {Promise<void>} A promise that resolves when Keycloak is initialized successfully.
 */
async function init() {
    try {
        await keycloak.init({ onLoad: 'check-sso', checkLoginIFrame: false });
    } catch (error) {
        logger.error('Keycloak init failed', error);
    }
}

/**
 * Initializes the Keycloak store.
 * @async
 * @function initStore
 * @param {Object} storeInstance - The store instance.
 * @returns {Promise<void>} A promise that resolves when the store is initialized successfully.
 */
async function initStore(storeInstance) {
    try {
        store = storeInstance;
        await store.initOauth(keycloak);
    } catch (error) {
        logger.error('Keycloak init failed', error);
    }
}

/**
 * Retrieves the user profile from Keycloak.
 * @async
 * @function getProfile
 * @returns {Promise<Object|null>} A promise that resolves with the user profile if the user is authenticated, otherwise null.
 */
async function getProfile() {
    try {
        if (keycloak.authenticated) {
            await keycloak.loadUserProfile();
             const attributes = keycloak.profile.attributes;
             const contactId = Array.isArray(attributes.contactId) && attributes.contactId.length > 0 ? parseInt(attributes.contactId[0], 10) : null;
             const fullName = `${keycloak.profile.firstName} ${keycloak.profile.lastName}`;
             const initials = `${keycloak.profile.firstName.charAt(0)}${keycloak.profile.lastName.charAt(0)}`.toUpperCase();
             const roles = keycloak.resourceAccess[import.meta.env.VITE_KEYCLOAK_CLIENT_ID].roles || [];
             const isAdminUser = roles.includes('ADMIN');

            const userProfile = {
                ...keycloak.profile,
                fullName: fullName,
                initials: initials,
                roles: roles,
                isAdminUser: isAdminUser,
                primaryOrganizationId: null,
                primaryOrganizationName: '',
                primaryOrganizationTypes: [],
                isHceOnlyUser: false,
                contactId: contactId
            };

            return userProfile;
        } else {
            return null;
        }
    } catch (error) {
        logger.error('Keycloak getUserProfile failed', error);
    }
}

/**
 * Logs out the user from Keycloak.
 * @async
 * @function logout
 * @returns {Promise<void>} A promise that resolves when the user is logged out successfully.
 */
async function logout() {
    try {
        await keycloak.logout();
    } catch (error) {
        logger.error('Keycloak logout failed', error);
    }
}


/**
 * Checks if the user has the "ADMIN" role.
 * @function isAdminUser
 * @returns {boolean} True if the user has the "ADMIN" role, otherwise false.
 */
function isAdminUser() {
    if (keycloak.resourceAccess && keycloak.resourceAccess[import.meta.env.VITE_KEYCLOAK_CLIENT_ID]) {
        return keycloak.resourceAccess[import.meta.env.VITE_KEYCLOAK_CLIENT_ID].roles.includes('ADMIN');
    }
    return false;
}

async function login(redirectUri) {
    const options = {};
    if (redirectUri) {
        options.redirectUri = redirectUri;
    } else {
        options.redirectUri = window.location.origin;
    }

    await keycloak.login(options);
}

async function refreshToken() {
    try {
        if (await keycloak.updateToken(300)) {
            return keycloak;
        } else {
            return null;
        }
    } catch (error) {
        logger.error('Keycloak refreshToken failed', error);
    }
}


/**
 * Keycloak service object.
 * @namespace KeycloakService
 */
const KeycloakService = {
    CallInit: init,
    CallInitStore: initStore,
    CallLogin: login,
    CallLogout: logout,
    CallTokenRefresh: refreshToken,
    GetUserProfile: getProfile,
    IsAdminUser: isAdminUser,
};

export default KeycloakService;